// homeSummaryAi.js — THE AI SOURCE of the Brief at the top of the home
// (AI_ASSISTANT_SPEC v1 §7 "the AI summary at the top of the home reads
// signals + vocabulary"; HOME_SIGNALS_CONTRACT v1 §2 rule 8; DESIGN-WL-
// ASSISTANT-MAIN-VIEW-1). The server half is lane current-23-aib:
//   sdk.execute('ai.boards', 'summary', { workspaceId, signals, locale, hour })
//     → { text, chips: [{ signalId, label, count, href }], generatedAt }
// where `signals` are the HOME_SIGNALS the shell collected (counts only), the
// chips are the server's deterministic top signals and every number in the
// text is guarded against the input counts.
//
// THE SWITCH: the Brief reads the AI answer when it lands within 800 ms of the
// signals settling; while it is in flight the Brief holds its skeleton; a
// slower answer, a failure or a service not there yet leaves the Brief on the
// deterministic template (functions/homeSummaryCompose.js) — and it stays on
// the template for those signals (no flip under the reader's eyes). A service
// that is not there is not asked again in this workspace this session.
// One answer per workspace + language + signals (a hash of id:count).
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained.

// The key of one ask: the workspace, the language and the signals' counts.
export const homeSummaryAiKey = function homeSummaryAiKey(root, list) {
  const r = root && typeof root === 'object' ? root : {}
  const w = r.workspace
  const ws = (w && String(w.id || w._id || '')) || ''
  const lang = String(r.lang || 'en')
  const sig = (Array.isArray(list) ? list : [])
    .map((s) => (s && s.id ? String(s.id) + ':' + String(s.count) : ''))
    .filter(Boolean)
    .join('|')
  let h = 0x811c9dc5
  for (let i = 0; i < sig.length; i++) {
    h ^= sig.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return ws + ':' + lang + ':' + (h >>> 0).toString(36)
}

// The state of the ask for these signals: null (never asked), or
// { key, status: 'loading'|'ready'|'slow'|'failed'|'unavailable', text, chips }.
export const homeSummaryAiState = function homeSummaryAiState(root, list) {
  const r = root && typeof root === 'object' ? root : {}
  const cur = r._homeSummaryAi
  if (!cur || typeof cur !== 'object') return null
  const key = this.call('homeSummaryAiKey', r, list)
  return cur.key === key ? cur : null
}

// Ask once for the settled signals of the Brief (never for the Signals style,
// never while the signals are still arriving). The 800 ms budget runs from
// the ask; the answer lands only while the ask is still the current one.
export const homeSummaryAiEnsure = function homeSummaryAiEnsure(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r || typeof r.update !== 'function') return null
  const look = this.call('homeLookRead', r) || {}
  if (look.summaryStyle !== 'brief') return null
  const view = this.call('homeSignalsView', r)
  const list = view && Array.isArray(view.list) ? view.list : []
  if (!view || view.waiting || !list.length) return null
  const key = this.call('homeSummaryAiKey', r, list)
  const cur = r._homeSummaryAi
  if (cur && cur.key === key) return cur
  const w = r.workspace
  const ws = (w && String(w.id || w._id || '')) || ''
  // A service that is not there is not asked again in this workspace.
  if (cur && cur.status === 'unavailable' && cur.ws === ws) {
    r.update({ _homeSummaryAi: { ...cur, key } }, { preventFetch: true })
    return r._homeSummaryAi
  }
  const sdk = this.sdk || (this.context && this.context.sdk) || this.call('getSDK')
  if (!sdk || typeof sdk.execute !== 'function') {
    r.update({ _homeSummaryAi: { key, ws, status: 'unavailable' } }, { preventFetch: true })
    return r._homeSummaryAi
  }
  const signals = list.map((s) => ({
    id: s.id,
    app: s.app,
    kind: s.kind,
    count: s.count,
    severity: s.severity,
    label: { ...(s.label || {}), text: this.call('homeSignalsText', s) },
    ...(s.href ? { href: s.href } : {})
  }))
  r.update({ _homeSummaryAi: { key, ws, status: 'loading', at: Date.now() } }, { preventFetch: true })
  const land = (patch) => {
    const now = r._homeSummaryAi
    if (!now || now.key !== key || now.status !== 'loading') return
    r.update({ _homeSummaryAi: { ...now, ...patch } }, { preventFetch: true })
  }
  const timer = setTimeout(() => land({ status: 'slow' }), 800)
  Promise.resolve()
    .then(() =>
      sdk.execute('ai.boards', 'summary', {
        ...(ws ? { workspaceId: ws } : {}),
        signals,
        locale: String(r.lang || 'en'),
        hour: new Date().getHours()
      })
    )
    .then(
      (res) => {
        clearTimeout(timer)
        const text = res && typeof res.text === 'string' ? res.text.trim() : ''
        if (!text) return land({ status: 'failed' })
        land({
          status: 'ready',
          text,
          chips: Array.isArray(res.chips) ? res.chips.slice(0, 5) : [],
          generatedAt: res.generatedAt ? Date.parse(res.generatedAt) || Date.now() : Date.now()
        })
      },
      (err) => {
        clearTimeout(timer)
        const msg = String((err && err.message) || err || '')
        land({
          status: /unknown entity|does not support|unknown method|unknown op|not found on service|is not available/i.test(msg)
            ? 'unavailable'
            : 'failed'
        })
      }
    )
  return r._homeSummaryAi
}

// The Brief's sentence from the AI answer: the text ALONE (design-d2's ruling
// 2026-10-01, after the deploy-11 sweep read the answer's chips running on
// after its last full stop). Its signals are homeSummaryAiChips — their own
// row under the sentence, never inline.
export const homeSummaryAiBrief = function homeSummaryAiBrief(state) {
  if (!state || state.status !== 'ready' || !state.text) return []
  return [[{ key: 'ai-text', text: state.text }]]
}

// The AI answer's signals: each chip the SAME local signal (same words, same
// list link), matched by id, once; a chip whose signal this shell does not
// hold is left out.
export const homeSummaryAiChips = function homeSummaryAiChips(state, list) {
  if (!state || state.status !== 'ready' || !state.text) return []
  const signals = Array.isArray(list) ? list : []
  const out = []
  for (const c of Array.isArray(state.chips) ? state.chips : []) {
    const sig = signals.find((s) => s && c && s.id === c.signalId)
    if (sig && !out.includes(sig)) out.push(sig)
  }
  return out
}
